import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, ShieldCheck, Menu as MenuIcon } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { StoreStatusBadge } from "./StoreStatusBadge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const Navbar = () => {
    const { count, setOpen } = useCart();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const linkCls = ({ isActive }) =>
        `font-heading uppercase text-sm tracking-wider transition-colors ${
            isActive ? "text-brand-red" : "text-white/80 hover:text-white"
        }`;

    return (
        <header
            data-testid="main-navbar"
            className="sticky top-0 z-40 border-b border-white/10 bg-[#0c0c0e]/80 backdrop-blur-xl"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[var(--neon-red)] to-[var(--neon-green)] grid place-items-center font-mono font-black text-black text-base">T</div>
                    <span className="headline text-xl text-white tracking-tight">THESSFOOD</span>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    <NavLink to="/" end className={linkCls} data-testid="nav-home">Αρχική</NavLink>
                    <NavLink to="/menu" className={linkCls} data-testid="nav-menu">Μενού</NavLink>
                    <NavLink to="/offers" className={linkCls} data-testid="nav-offers">
                        <span className="relative">
                            Προσφορές
                            <span className="absolute -top-2 -right-3 h-1.5 w-1.5 bg-brand-red rounded-full animate-pulse"></span>
                        </span>
                    </NavLink>
                    <NavLink to="/kids" className={linkCls} data-testid="nav-kids">
                        Kids Menu
                    </NavLink>
                    <NavLink to="/orders" className={linkCls} data-testid="nav-orders">Παραγγελίες</NavLink>
                    {user?.role === "admin" && (
                        <NavLink to="/admin" className={linkCls} data-testid="nav-admin">Admin</NavLink>
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <StoreStatusBadge />
                    <Link
                        to="/menu"
                        data-testid="nav-order-cta"
                        className="hidden sm:inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redhover text-white px-5 h-10 rounded-sm font-heading uppercase tracking-wider text-xs font-bold transition hover:-translate-y-0.5 active:scale-95 shadow-lg shadow-brand-red/20"
                    >
                        Παραγγελία Τώρα
                    </Link>
                    <button
                        data-testid="open-cart-btn"
                        onClick={() => setOpen(true)}
                        className="relative h-10 w-10 grid place-items-center border border-white/15 hover:border-brand-red rounded-sm transition"
                        aria-label="Καλάθι"
                    >
                        <ShoppingCart size={18} />
                        {count > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-2 -right-2 h-5 min-w-5 px-1 bg-brand-red text-white text-xs font-bold grid place-items-center rounded-full"
                            >
                                {count}
                            </span>
                        )}
                    </button>

                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    data-testid="user-menu-trigger"
                                    variant="ghost"
                                    className="hidden sm:flex font-heading uppercase tracking-wider"
                                >
                                    <User size={16} className="mr-2" /> {user.name.split(" ")[0]}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#16161a] border-white/10 text-white">
                                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-my-orders">
                                    Οι παραγγελίες μου
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                                        <ShieldCheck size={14} className="mr-2" /> Admin Panel
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                                    <LogOut size={14} className="mr-2" /> Αποσύνδεση
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Link to="/login" className="hidden sm:inline-block btn-outline !py-2 !px-4 text-xs" data-testid="nav-login">
                                Σύνδεση
                            </Link>
                            <Link to="/register" className="btn-primary !py-2 !px-4 text-xs" data-testid="nav-register">
                                Εγγραφή
                            </Link>
                        </>
                    )}

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="md:hidden h-10 w-10 grid place-items-center border border-white/15 rounded-sm"
                                aria-label="Μενού"
                                data-testid="mobile-menu-trigger"
                            >
                                <MenuIcon size={18} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#16161a] border-white/10 text-white md:hidden">
                            <DropdownMenuItem onClick={() => navigate("/")}>Αρχική</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate("/menu")}>Μενού</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate("/offers")}>Προσφορές</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate("/kids")}>Kids Menu</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate("/orders")}>Παραγγελίες</DropdownMenuItem>
                            {user?.role === "admin" && (
                                <DropdownMenuItem onClick={() => navigate("/admin")}>Admin</DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
};
